import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';

import { CalendarService } from '../services/calendar.service';

import {
  addEvent,
  addEventFailure,
  addEventSuccess,
  fetchEvents,
  fetchEventsFailure,
  fetchEventsSuccess,
  fetchNewEventCanHaveASecondMeetingDate,
  fetchNewEventCanHaveASecondMeetingDateFailure,
  fetchNewEventCanHaveASecondMeetingDateSuccess,
  fetchNewEventOrgans,
  fetchNewEventOrgansFailure,
  fetchNewEventOrgansSuccess,
  fetchOrgans,
  fetchOrgansFailure,
  fetchOrgansSuccess,
  fetchUnscheduledEvents,
  fetchUnscheduledEventsFailure,
  fetchUnscheduledEventsSuccess,
  fetchUserCanCreateEvent,
  fetchUserCanCreateEventFailure,
  fetchUserCanCreateEventSuccess,
  fetchUserCanSeeUnscheduledEvents,
  fetchUserCanSeeUnscheduledEventsFailure,
  fetchUserCanSeeUnscheduledEventsSuccess,
  removeEvents,
  removeEventsFailure,
  removeEventsSuccess,
  updateSelectedOrgans,
  updateSelectedOrgansFailure,
  updateSelectedOrgansSuccess
} from './calendar.actions';

import { addHoursUntilMaxEndOfDay } from '~/calendar/calendar/calendar.helpers';

@Injectable()
export class CalendarEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly calendarService: CalendarService,
    private messageService: MessageService,
    private translateService: TranslateService
  ) {}

  fetchEvents$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchEvents),
        switchMap((action) =>
          this.calendarService.fetchEvents(action.data).pipe(
            map((data) =>
              fetchEventsSuccess({
                data: data.map((item) => ({
                  ...item,
                  start: new Date(item.start),
                  end: item.end ? new Date(item.end) : addHoursUntilMaxEndOfDay(new Date(item.start), 2)
                }))
              })
            ),
            catchError((error: any) => of(fetchEventsFailure({ error })))
          )
        )
      )
  );

  fetchUnscheduledEvents$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchUnscheduledEvents),
        switchMap(() =>
          this.calendarService.fetchUnscheduledEvents().pipe(
            map((data) => fetchUnscheduledEventsSuccess({ data })),
            catchError((error: any) => of(fetchUnscheduledEventsFailure({ error })))
          )
        )
      )
  );

  fetchOrgans$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchOrgans),
        switchMap(() =>
          this.calendarService.fetchOrgans().pipe(
            map((data) => fetchOrgansSuccess({ data })),
            catchError((error: any) => of(fetchOrgansFailure({ error })))
          )
        )
      )
  );

  fetchNewEventOrgans$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchNewEventOrgans),
        switchMap(() =>
          this.calendarService.fetchNewEventOrgans().pipe(
            map((data) => fetchNewEventOrgansSuccess({ data })),
            catchError((error: any) => of(fetchNewEventOrgansFailure({ error })))
          )
        )
      )
  );

  updateSelectedOrgans$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(updateSelectedOrgans),
        switchMap((action) =>
          this.calendarService.updateSelectedOrgans(action.data).pipe(
            map((data) => updateSelectedOrgansSuccess({ data })),
            catchError((error: any) => of(updateSelectedOrgansFailure({ error })))
          )
        )
      )
  );

  addEvent$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(addEvent),
        switchMap((action) =>
          this.calendarService.addEvent(action.data).pipe(
            map((data) =>
              addEventSuccess({
                data: data.map((event) => ({
                  ...event,
                  start: new Date(event.start),
                  end: event.end ? new Date(event.end) : undefined
                }))
              })
            ),
            tap(() => {
              this.messageService.add({
                severity: 'success',
                detail: this.translateService.instant('calendar.add.success'),
                life: 3000
              });
            }),
            catchError((error: any) => {
              this.messageService.add({
                severity: 'error',
                detail: this.translateService.instant('calendar.add.failure'),
                life: 3000
              });
              return of(addEventFailure({ error }));
            })
          )
        )
      )
  );

  removeEvents$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(removeEvents),
        switchMap((action) =>
          this.calendarService.removeEvents(action.data).pipe(
            map(() => removeEventsSuccess({ data: action.data })),
            tap(() => {
              this.messageService.add({
                severity: 'success',
                detail: this.translateService.instant('calendar.remove.success'),
                life: 3000
              });
            }),
            catchError((error: any) => {
              this.messageService.add({
                severity: 'error',
                detail: this.translateService.instant('calendar.remove.failure'),
                life: 3000
              });
              return of(removeEventsFailure({ error }));
            })
          )
        )
      )
  );

  fetchUserCanCreateMeeting = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchUserCanCreateEvent),
        switchMap(() =>
          this.calendarService.fetchUserCanCreateMeeting().pipe(
            map((data) => fetchUserCanCreateEventSuccess({ data })),
            catchError((error: any) => of(fetchUserCanCreateEventFailure({ error })))
          )
        )
      )
  );

  fetchUserCanSeeUnscheduledEvents = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchUserCanSeeUnscheduledEvents),
        switchMap(() =>
          this.calendarService.fetchUserCanSeeUnscheduledEvents().pipe(
            map((data) => fetchUserCanSeeUnscheduledEventsSuccess({ data })),
            catchError((error: any) => of(fetchUserCanSeeUnscheduledEventsFailure({ error })))
          )
        )
      )
  );

  fetchNewEventCanHaveASecondMeetingDate = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(fetchNewEventCanHaveASecondMeetingDate),
        switchMap(() =>
          this.calendarService.fetchNewEventCanHaveASecondMeetingDate().pipe(
            map((data) => fetchNewEventCanHaveASecondMeetingDateSuccess({ data })),
            catchError((error: any) => of(fetchNewEventCanHaveASecondMeetingDateFailure({ error })))
          )
        )
      )
  );
}
